import React, { useEffect, useState, useCallback } from 'react'
import { X, MessageSquare, User, ChevronLeft, ChevronRight } from 'lucide-react'
import type { ContactTag, Conversation } from '../../types/conversation.types'
import type { Client } from '../../services/clientService'
import { conversationService } from '../../services/conversationService'
import { clientService } from '../../services/clientService'
import { logger } from '../../utils/logger'

const PAGE_SIZE = 15

export interface TagUsageExplorerModalProps {
  isOpen: boolean
  onClose: () => void
  tag: ContactTag
  mode: 'conversations' | 'clients'
  /** Ao clicar numa conversa na lista (modo conversations). */
  onOpenConversation?: (conversationId: number) => void
}

export const TagUsageExplorerModal: React.FC<TagUsageExplorerModalProps> = ({
  isOpen,
  onClose,
  tag,
  mode,
  onOpenConversation
}) => {
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [totalPages, setTotalPages] = useState(1)

  const load = useCallback(async () => {
    if (!isOpen) return
    setLoading(true)
    try {
      const tagParam = String(tag.id)
      if (mode === 'conversations') {
        const res = await conversationService.getConversations({
          include_finished: true,
          tag_ids: tagParam,
          tag_match: 'all',
          include_tags: true,
          page,
          limit: PAGE_SIZE
        })
        if (res.success && res.data) {
          setConversations(res.data.conversations || [])
          setTotalPages(res.data.pagination?.totalPages ?? 1)
        }
      } else {
        const res = await clientService.listClients({
          page,
          limit: PAGE_SIZE,
          tag_ids: tagParam,
          tag_match: 'all',
          include_tags: true
        })
        if (res.success && res.data) {
          setClients(res.data.clients || [])
          setTotalPages(res.data.pagination?.totalPages ?? 1)
        }
      }
    } catch (e) {
      logger.error('TagUsageExplorerModal load:', e)
    } finally {
      setLoading(false)
    }
  }, [isOpen, mode, tag.id, page])

  useEffect(() => {
    if (isOpen) {
      setPage(1)
    }
  }, [isOpen, mode, tag.id])

  useEffect(() => {
    load()
  }, [load])

  if (!isOpen) return null

  const title = mode === 'conversations' ? 'Conversas com esta tag' : 'Clientes com esta tag'

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col border border-gray-200"
        role="dialog"
        aria-labelledby="tag-explorer-title"
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-100">
          <div className="min-w-0">
            <h2 id="tag-explorer-title" className="text-lg font-semibold text-gray-900">
              {title}
            </h2>
            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
              <span
                className="inline-block w-3 h-3 rounded-full shrink-0 border border-gray-200"
                style={{ backgroundColor: tag.color }}
              />
              <span className="truncate">{tag.name}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-sm text-gray-500 text-center py-8">Carregando...</p>
          ) : mode === 'conversations' ? (
            conversations.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">Nenhuma conversa encontrada com esta tag.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {conversations.map(c => (
                  <li key={c.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <MessageSquare className="w-4 h-4 text-gray-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {c.client?.name || 'Cliente'} · #{c.id}
                        </p>
                        <p className="text-xs text-gray-500">{c.client?.phone_number}</p>
                        <p className="text-xs text-gray-400 capitalize">{c.status}</p>
                      </div>
                    </div>
                    {onOpenConversation && (
                      <button
                        type="button"
                        onClick={() => {
                          onOpenConversation(c.id)
                          onClose()
                        }}
                        className="text-sm text-blue-600 hover:underline shrink-0"
                      >
                        Abrir conversa
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )
          ) : clients.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">Nenhum cliente encontrado com esta tag.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {clients.map(cl => (
                <li key={cl.id} className="py-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{cl.name || '—'}</p>
                    <p className="text-xs text-gray-500">{cl.phone_number}</p>
                    <p className="text-xs text-gray-400">ID {cl.id}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/80">
            <span className="text-xs text-gray-600">
              Página {page} de {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <div className="p-4 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}
